import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { BooksActivityEntry } from "@/api/types";

export type BooksActivityStatus = "loading" | "ready" | "error";

export interface BooksActivityFilters {
  source?: string;
  accountId?: string;
  from?: string;
  to?: string;
}

export interface UseBooksActivityResult {
  /** Empty on first load and on failure — a real "nothing in this window" is a
   *  different answer from "we do not know yet", same reasoning as `useLabourReport`. */
  entries: BooksActivityEntry[];
  status: BooksActivityStatus;
  error: string | null;
  /** Re-fetch with a new set of filters. Passing nothing (or omitting a field) asks
   *  the server for its own default — unbounded source/account, its own default window. */
  load: (filters?: BooksActivityFilters) => Promise<void>;
}

/**
 * GET /api/demo/books/activity — Books' Activity tab, ef-042-0a. Owner-only server-side;
 * this hook does not check that and must not — same discipline `useLabourReport` and
 * `useHoursReport` already follow: a client-side role check is a rendering decision, and
 * treating it as the control is how the real one (the server's 403) stops being
 * maintained.
 *
 * A failure is NOT degraded to an empty list — same reasoning as `useLabourReport`: an
 * empty result for a filter set that truly has none is a different, honest answer from
 * "the request failed", and collapsing the two would render a confident, empty feed.
 */
export function useBooksActivity(initialFilters?: BooksActivityFilters): UseBooksActivityResult {
  const [entries, setEntries] = useState<BooksActivityEntry[]>([]);
  const [status, setStatus] = useState<BooksActivityStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const fetchInto = useCallback(
    async (filters?: BooksActivityFilters, signal?: AbortSignal): Promise<void> => {
      setStatus("loading");
      try {
        const resolved = await demoApi.getBooksActivity(filters, signal);
        if (signal?.aborted) return;
        setEntries(resolved);
        setStatus("ready");
        setError(null);
      } catch (err) {
        if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        // The PREVIOUS list is cleared, not kept — a stale feed under filters just
        // asked for is the one outcome that reads as an answer to that question.
        setEntries([]);
        setStatus("error");
        setError(problemMessage(err, "Could not load the activity feed."));
      }
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchInto(initialFilters, controller.signal);
    return () => controller.abort();
    // initialFilters is read once, on mount — deliberately absent from the dependency
    // array. Every later filter change goes through `load()` instead, same discipline
    // `useLabourReport`'s identical comment gives for `initialRange`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInto]);

  const load = useCallback(
    (filters?: BooksActivityFilters) => fetchInto(filters),
    [fetchInto],
  );

  return { entries, status, error, load };
}
